/**
 * R18-5 — Camera Requests Tray.
 *
 * Staff-only floating tray showing pending audience camera requests. Mirrors
 * the visual + interaction pattern of HostApprovalTray for cohesion.
 *
 * Props:
 *   - isStaff   (boolean)
 *   - requests  (array of {request_id, user_id, display_name, note, created_at})
 *   - onApprove ({request_id, user_id}) => void
 *   - onDeny    ({request_id, user_id}) => void
 *   - liveCameraCount (number) — current live camera count for cap display
 *   - liveCameraCap   (number) — max_live_cameras for the room
 *
 * Testids:
 *   - camera-requests-tray
 *   - camera-request-card-{request_id}
 *   - camera-request-approve-{request_id}
 *   - camera-request-deny-{request_id}
 */
import React, { useMemo } from "react";
import { Camera, Check, X } from "lucide-react";


export default function CameraRequestsTray({
  isStaff, requests, onApprove, onDeny, liveCameraCount, liveCameraCap,
}) {
  const visible = useMemo(
    () => isStaff && Array.isArray(requests) && requests.length > 0,
    [isStaff, requests],
  );
  if (!visible) {
    return <div data-testid="camera-requests-tray" className="hidden" />;
  }
  const atCap = typeof liveCameraCount === "number"
    && typeof liveCameraCap === "number"
    && liveCameraCount >= liveCameraCap;
  return (
    <div
      className="fixed top-20 right-4 z-40 w-[340px] max-w-[88vw] flex flex-col gap-2"
      data-testid="camera-requests-tray"
      aria-label="Camera requests"
    >
      <div className="px-3 py-1.5 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-cyan-300/85 bg-cyan-500/8 border border-cyan-500/30 rounded-md backdrop-blur">
        <Camera className="w-3.5 h-3.5" />
        <span className="font-bold">Camera requests</span>
        {typeof liveCameraCap === "number" && (
          <span className="ml-auto opacity-75" data-testid="camera-requests-cap">
            {liveCameraCount ?? 0}/{liveCameraCap} live
          </span>
        )}
      </div>
      {atCap && (
        <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-amber-300 bg-amber-500/8 border border-amber-500/30 rounded">
          Live wall full — approve to swap, or expand max live cameras.
        </div>
      )}
      {requests.map((r) => (
        <RequestCard key={r.request_id} r={r} onApprove={onApprove} onDeny={onDeny} />
      ))}
    </div>
  );
}


function RequestCard({ r, onApprove, onDeny }) {
  return (
    <div
      className="rounded-lg border border-cyan-400/25 bg-[#0b1418]/95 backdrop-blur p-3 shadow-[0_8px_28px_rgba(0,0,0,0.55)]"
      data-testid={`camera-request-card-${r.request_id}`}
    >
      <div className="flex items-start gap-2 mb-2">
        <div className="w-7 h-7 rounded grid place-items-center bg-cyan-500/15 text-cyan-300 shrink-0">
          <Camera className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate" data-testid={`camera-request-name-${r.request_id}`}>
            {r.display_name || r.user_id}
          </div>
          <div className="text-[10px] text-white/45 mt-0.5">wants on cam</div>
        </div>
      </div>
      {r.note && (
        <p className="text-xs text-white/75 leading-snug mb-2.5" data-testid={`camera-request-note-${r.request_id}`}>
          &ldquo;{r.note}&rdquo;
        </p>
      )}
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => onApprove({ request_id: r.request_id, user_id: r.user_id })}
          className="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded text-[10px] font-bold uppercase tracking-[0.14em] bg-emerald-500/90 hover:bg-emerald-500 text-white transition"
          data-testid={`camera-request-approve-${r.request_id}`}
        >
          <Check className="w-3 h-3" /> Approve
        </button>
        <button
          onClick={() => onDeny({ request_id: r.request_id, user_id: r.user_id })}
          className="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded text-[10px] font-bold uppercase tracking-[0.14em] bg-white/10 hover:bg-white/15 text-white/85 transition"
          data-testid={`camera-request-deny-${r.request_id}`}
        >
          <X className="w-3 h-3" /> Deny
        </button>
      </div>
    </div>
  );
}
